import Logger from "../../logger"
import Entity from "../../model/Entity"
import { HackleUser } from "../../model/model"
import { WorkspaceMetadata } from "../Workspace"
import { WorkspaceManager } from "../WorkspaceManager"
import WorkspaceEvaluationFetcher from "./WorkspaceEvaluationFetcher"
import WorkspaceEvaluation from "./WorkspaceEvaluation"
import { WorkspaceEvaluationCache } from "./WorkspaceEvaluationCache"
import { WorkspaceEvaluationStorage } from "./WorkspaceEvaluationStorage"
import { WorkspaceEvaluationContext } from "./WorkspaceEvaluationContext"
import { RemoteEvaluateContext } from "./model/RemoteEvaluateContext"
import FullWorkspaceRemoteEvaluator from "./evaluator/full/FullWorkspaceRemoteEvaluator"
import { FullWorkspaceEvaluateRequest } from "./evaluator/full/FullWorkspaceEvaluateRequest"
import PartialWorkspaceRemoteEvaluator from "./evaluator/partial/PartialWorkspaceRemoteEvaluator"
import { PartialWorkspaceEvaluateRequest } from "./evaluator/partial/PartialWorkspaceEvaluateRequest"

/**
 * Owns the per-user evaluation cache and its persistence. `sync` refreshes the whole workspace for
 * a user (the full evaluator resolves FULL/DELTA/not-modified into a cached context, which is stored
 * here); `workspace` reads the cache synchronously; `evaluate` resolves a specific set of entities
 * without touching the cache.
 */
export default class WorkspaceEvaluationManager implements WorkspaceManager, WorkspaceEvaluationFetcher {
  constructor(
    private readonly fullEvaluator: FullWorkspaceRemoteEvaluator,
    private readonly partialEvaluator: PartialWorkspaceRemoteEvaluator,
    private readonly storage: WorkspaceEvaluationStorage,
    private readonly cache: WorkspaceEvaluationCache
  ) {}

  initialize(): void {
    this.load()
  }

  metadata(): WorkspaceMetadata | null {
    return this.cache.latest()?.workspace.metadata ?? null
  }

  workspace(user: HackleUser): WorkspaceEvaluation | null {
    const key = WorkspaceEvaluationContext.keyOf(user)
    return this.cache.get(key)?.workspace ?? null
  }

  async sync(context: RemoteEvaluateContext): Promise<void> {
    const base = this.cache.get(context.key) ?? null
    const request = FullWorkspaceEvaluateRequest.of(context, base)
    try {
      const response = await this.fullEvaluator.evaluate(request)
      this.store(response.context)
    } catch (e) {
      Logger.log.error(`Failed to sync WorkspaceEvaluation: ${e}`)
    }
  }

  async evaluate(context: RemoteEvaluateContext, entities: Entity[]): Promise<WorkspaceEvaluation> {
    const request = new PartialWorkspaceEvaluateRequest(context, entities)
    const response = await this.partialEvaluator.evaluate(request)
    return response.evaluation
  }

  private store(context: WorkspaceEvaluationContext): void {
    const snapshots = this.cache.put(context)
    this.storage.set(snapshots)
  }

  private load(): void {
    try {
      const records = this.storage.get()
      this.cache.restore(records)
    } catch (e) {
      Logger.log.error(`Failed to load WorkspaceEvaluation from local: ${e}`)
    }
  }
}
